import React, { useState } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Paper,
    IconButton,
    useTheme,
    useMediaQuery,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip
} from '@mui/material';
import SEOHead from '../../components/SEOHead';
import SiteHeader from '../../components/SiteHeader';
import AppFooter from '../../components/AppFooter';
import faqContent from '../../content/faq.json';
import {
    ArrowBack,
    ExpandMore,
    Help,
    Security,
    Favorite,
    Phone,
    Settings
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const LIGHT_PURPLE = '#A76FFF';

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
    'Getting Started': <Help sx={{ color: LIGHT_PURPLE }} />,
    'How It Works': <Settings sx={{ color: LIGHT_PURPLE }} />,
    'Safety & Privacy': <Security sx={{ color: LIGHT_PURPLE }} />,
    'Dating & Matches': <Favorite sx={{ color: LIGHT_PURPLE }} />,
    'Technical Support': <Phone sx={{ color: LIGHT_PURPLE }} />,
};

const WebFAQScreen: React.FC = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expanded, setExpanded] = useState<string | false>(false);

    const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    // Questions and answers live in faq.json so the build can also write them into the page's
    // crawlable HTML (scripts/generate-static-seo-pages.js)
    const faqCategories = faqContent.map(({ category, questions }) => ({
        category,
        icon: CATEGORY_ICONS[category] ?? <Help sx={{ color: LIGHT_PURPLE }} />,
        questions,
    }));

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqCategories.flatMap(category => 
            category.questions.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        )
    };

    return (
        <>
            <SEOHead
                title="Frequently Asked Questions | Pop The Balloon"
                description="Find answers about Daily Picks, Pop or Keep matching, account safety, subscriptions and using the Pop The Balloon dating app."
                canonical="https://www.poptheballoonapp.com/faq"
                ogTitle="Pop The Balloon FAQ - Your Questions Answered"
                ogDescription="Get answers to common questions about Pop The Balloon dating app, including how it works, safety features, and getting started."
                ogUrl="https://www.poptheballoonapp.com/faq"
            />
            
            {/* Structured Data */}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(structuredData)
                }}
            />

            <SiteHeader />
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <IconButton onClick={() => navigate(-1)} size="large" sx={{ mr: 1 }}>
                            <ArrowBack />
                        </IconButton>
                        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                            Frequently Asked Questions
                        </Typography>
                    </Box>

                    {/* Intro */}
                    <Box sx={{ mb: 5, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                            Got questions? We've got answers!
                        </Typography>
                        <Typography variant="body1" sx={{ maxWidth: '600px', mx: 'auto', lineHeight: 1.6 }}>
                            Everything you need to know about Pop The Balloon dating app, from getting started 
                            to finding your perfect match through our unique balloon-popping format.
                        </Typography>
                    </Box>

                    {/* FAQ Categories */}
                    {faqCategories.map((category, categoryIndex) => (
                        <Box key={categoryIndex} sx={{ mb: 4 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                {category.icon}
                                <Typography 
                                    variant="h6" 
                                    sx={{ ml: 2, fontWeight: 600, color: LIGHT_PURPLE }}
                                >
                                    {category.category}
                                </Typography>
                            </Box>
                            
                            {category.questions.map((faq) => (
                                <Accordion
                                    key={faq.id}
                                    expanded={expanded === faq.id}
                                    onChange={handleChange(faq.id)}
                                    sx={{ 
                                        mb: 1,
                                        borderRadius: 1,
                                        '&:before': { display: 'none' },
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                    }}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        sx={{
                                            bgcolor: expanded === faq.id ? 'rgba(167, 111, 255, 0.05)' : 'transparent',
                                            '&:hover': {
                                                bgcolor: 'rgba(167, 111, 255, 0.05)'
                                            }
                                        }}
                                    >
                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {faq.question}
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Typography 
                                            variant="body1" 
                                            sx={{ 
                                                color: 'text.secondary',
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {faq.answer}
                                        </Typography>
                                    </AccordionDetails>
                                </Accordion>
                            ))}
                        </Box>
                    ))}

                    {/* Contact Section */}
                    <Box sx={{ 
                        mt: 6, 
                        p: 3, 
                        bgcolor: 'rgba(167, 111, 255, 0.05)', 
                        borderRadius: 2,
                        textAlign: 'center'
                    }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            Still have questions?
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                            Our support team is here to help! Reach out anytime.
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                href="mailto:support@poptheballoonapp.com"
                                sx={{
                                    bgcolor: LIGHT_PURPLE,
                                    '&:hover': { bgcolor: '#8A5CF5' }
                                }}
                            >
                                Email Support
                            </Button>
                            <Button
                                variant="outlined"
                                href="https://discord.gg/ynaRHUNQkx"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{
                                    borderColor: LIGHT_PURPLE,
                                    color: LIGHT_PURPLE,
                                    '&:hover': { 
                                        borderColor: '#8A5CF5',
                                        bgcolor: 'rgba(167, 111, 255, 0.05)'
                                    }
                                }}
                            >
                                Join Discord
                            </Button>
                        </Box>
                    </Box>
                </Paper>
            </Container>
            <AppFooter />
        </>
    );
};

export default WebFAQScreen;